import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  BOLT_STAR_HOW_IT_WORKS_ROUTE,
  BoltStarAnalyticsEvents,
  OnboardingPopupVariant,
  useTrackBoltStarEvent,
  PopupClosingType,
} from '@provider-portal/mp2-common/topPerformer';

import { BoltStarOnboardingDialog } from './BoltStarOnboardingDialog';
import { BoltStarPageOnboardingDialog } from './BoltStarPageOnboardingDialog';
import { dismissOnboardingDialog, isOnboardingDialogDismissed } from './onboardingDialogDismissals';

export interface BoltStarOnboardingDialogContainerProps {
  variant: OnboardingPopupVariant;
  /** Whether the current page is one this variant belongs on. */
  isActive: boolean;
}

export function BoltStarOnboardingDialogContainer({
  variant,
  isActive,
}: BoltStarOnboardingDialogContainerProps) {
  const history = useHistory();
  const trackEvent = useTrackBoltStarEvent();

  // Dismissing keeps the dialog mounted so it closes with its exit animation. Re-read on every
  // activation, since the other variant or a How it works visit may have retired this one since.
  // This happens during render so a stale value never opens the dialog for a frame.
  const [isDismissed, setIsDismissed] = useState(() => isOnboardingDialogDismissed(variant));
  const [wasActive, setWasActive] = useState(isActive);
  if (isActive !== wasActive) {
    setWasActive(isActive);
    if (isActive) {
      setIsDismissed(isOnboardingDialogDismissed(variant));
    }
  }

  const isOpen = isActive && !isDismissed;

  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      trackEvent(BoltStarAnalyticsEvents.ONBOARDING_POPUP_SHOWN, { popup_variant: variant });
    }
    wasOpen.current = isOpen;
  }, [isOpen, trackEvent, variant]);

  const dismiss = (closingType: PopupClosingType) => {
    trackEvent(BoltStarAnalyticsEvents.ONBOARDING_POPUP_CLOSED, {
      popup_variant: variant,
      closing_type: closingType,
    });
    dismissOnboardingDialog(variant);
    setIsDismissed(true);
  };

  const handleClose = () => dismiss(PopupClosingType.CLOSE_BUTTON);

  const handleDismiss = () => dismiss(PopupClosingType.DISMISS_BUTTON);

  const handleAction = () => {
    dismiss(PopupClosingType.CALL_TO_ACTION);
    history.push(BOLT_STAR_HOW_IT_WORKS_ROUTE);
  };

  if (variant === OnboardingPopupVariant.BOLT_STAR_PAGE) {
    return (
      <BoltStarPageOnboardingDialog isOpen={isOpen} onClose={handleClose} onAction={handleAction} />
    );
  }

  return (
    <BoltStarOnboardingDialog
      isOpen={isOpen}
      onClose={handleClose}
      onDismiss={handleDismiss}
      onAction={handleAction}
    />
  );
}
